// API utility functions for consistent API calls

// Get API base URL from environment or use proxy (relative URL)
const API_BASE_URL = process.env.REACT_APP_API_URL || '';

// Debug logging
console.log('API_BASE_URL configured as:', API_BASE_URL || '(using proxy - relative URLs)');

/**
 * Make an API call with proper base URL and authentication
 * @param {string} endpoint - API endpoint (e.g., '/api/admin/tenants')
 * @param {Object} options - Fetch options
 * @returns {Promise<Object>} Response with data and ok properties
 */
export const apiCall = async (endpoint, options = {}) => {
  // Use relative URL if proxy is configured, otherwise use full URL
  const url = API_BASE_URL ? `${API_BASE_URL}${endpoint}` : endpoint;
  
  // Debug logging
  console.log('Making API call to:', url);
  
  // Add default headers
  const headers = {
    ...options.headers,
  };

  // Only set Content-Type for non-FormData requests
  if (!(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }

  // Add authorization header if token exists
  const token = localStorage.getItem('token');
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(url, {
    ...options,
    headers,
  });

  // Parse JSON response if possible
  let data = null;
  try {
    data = await response.json();
  } catch (error) {
    // Response is not JSON, use text instead
    data = await response.text();
  }

  return {
    ok: response.ok,
    status: response.status,
    statusText: response.statusText,
    data: data,
    headers: response.headers,
  };
};

/**
 * Make a GET request
 * @param {string} endpoint - API endpoint
 * @param {Object} params - Query parameters
 * @returns {Promise<Object>} Response object
 */
export const apiGet = async (endpoint, params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  const fullEndpoint = queryString ? `${endpoint}?${queryString}` : endpoint;
  return apiCall(fullEndpoint, { method: 'GET' });
};

/**
 * Make a POST request
 * @param {string} endpoint - API endpoint
 * @param {Object|FormData} data - Request body
 * @returns {Promise<Object>} Response object
 */
export const apiPost = async (endpoint, data) => {
  // Check if data is FormData (for file uploads)
  if (data instanceof FormData) {
    // For FormData, don't set Content-Type (let browser set it with boundary)
    const options = {
      method: 'POST',
      body: data,
    };
    
    // Add authorization header if token exists
    const token = localStorage.getItem('token');
    if (token) {
      options.headers = {
        Authorization: `Bearer ${token}`,
      };
    }
    
    return apiCall(endpoint, options);
  } else {
    // For JSON data, use the existing logic
    return apiCall(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }
};

/**
 * Make a PUT request
 * @param {string} endpoint - API endpoint
 * @param {Object} data - Request body
 * @returns {Promise<Object>} Response object
 */
export const apiPut = async (endpoint, data) => {
  return apiCall(endpoint, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
};

/**
 * Make a PATCH request
 * @param {string} endpoint - API endpoint
 * @param {Object} data - Request body
 * @returns {Promise<Object>} Response object
 */
export const apiPatch = async (endpoint, data) => {
  return apiCall(endpoint, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
};

/**
 * Make a DELETE request
 * @param {string} endpoint - API endpoint
 * @returns {Promise<Object>} Response object
 */
export const apiDelete = async (endpoint) => {
  return apiCall(endpoint, { method: 'DELETE' });
};

/**
 * Get the full API URL for an endpoint
 * @param {string} endpoint - API endpoint
 * @returns {string} Full URL
 */
export const getApiUrl = (endpoint) => {
  return `${API_BASE_URL}${endpoint}`;
};

export default {
  apiCall,
  apiGet,
  apiPost,
  apiPut,
  apiPatch,
  apiDelete,
  getApiUrl,
};
